'use client';

import ImageSB, { ImageSBProps } from '@/components/atoms/image/ImageSB';
import LinkButtonSB, {
  LinkButtonSBProps,
} from '@/components/atoms/linkButton/LinkSB';
import RichTextFieldSB, {
  RichTextFieldSBProps,
} from '@/components/atoms/richTextField/RichTextFieldSB';
import { Text } from '@/components/atoms/text';
import { BlockComponentModel } from '@/components/cms/types/blok-component';
import { SECTION_NAMES } from '@/components/integrations/trackingScripts/constants';
import { Accordion } from '@/components/molecules/accordion';
import { AccordionListComponent } from '@/components/molecules/accordion/AccordionList';
import { useAccordion } from '@/components/molecules/accordion/AccordionProvider';

import { ImageAccordions } from '@/features/textMedia/imageAccordions';
import {
  BlockHeader,
  BlockHeaderContent,
} from '@/features/utils/BlockHeaderContent';
import { BlockWrapper } from '@/features/utils/BlockWrapper';

export interface ImageAccordionsStrapiProps {
  image: ImageSBProps;
  header?: BlockHeader;
  accordions: {
    title: string;
    description: RichTextFieldSBProps;
    button?: LinkButtonSBProps;
    image?: ImageSBProps;
  }[];
}

function ImageAccordionsStrapiComponent({
  block,
  ...rest
}: BlockComponentModel<ImageAccordionsStrapiProps>) {
  const { header, accordions, image: mainImage } = block;
  const { openItemId } = useAccordion();

  const imageToRender = accordions[Number(openItemId)]?.image ?? mainImage;

  const formattedAccordionList =
    accordions?.map((item) => ({
      header: <Text variant='subtitle-l'>{item.title}</Text>,
      children: (
        <div className='flex flex-col gap-6'>
          <RichTextFieldSB block={item.description} />
          {item?.button && (
            <LinkButtonSB
              block={item.button}
              inner={{ classNames: { root: 'w-full md:w-fit' } }}
            />
          )}
        </div>
      ),
    })) || [];

  return (
    <BlockWrapper {...rest} theme={header?.theme?.themeKey}>
      <div data-tracker-section={SECTION_NAMES.IMAGE_ACCORDIONS}>
        <BlockHeaderContent {...header}>
          <div className='flex w-full gap-6'>
            <ImageAccordions
              image={
                <ImageSB
                  block={imageToRender}
                  inner={{ classNames: { image: 'rounded-xl' }, width: 360 }}
                />
              }
            >
              <AccordionListComponent accordionList={formattedAccordionList} />
            </ImageAccordions>
          </div>
        </BlockHeaderContent>
      </div>
    </BlockWrapper>
  );
}

export default function ImageAccordionsStrapi(
  props: BlockComponentModel<ImageAccordionsStrapiProps>
) {
  return (
    <Accordion>
      <ImageAccordionsStrapiComponent {...props} />
    </Accordion>
  );
}
